import scroll from '@common/scroll/scroll'
import DOM from '@common/DOM/DOM'

import animateModalPart from './animateModalPart'

import type { IBaseModalProps } from './definitions'
import { ESize, EOverlay, EModalEvent } from './definitions'

import { getActModalId, increaseModalId } from './utils'

import styles from './Modal.css'

/**
 * Factory a modal példányosításáházo.
 * @param props - JSX property-k {@link IBaseModalProps}.
 */
export default function Modal (props: IBaseModalProps): HTMLElement {
  increaseModalId()

  let modal: HTMLElement
  let window: HTMLElement
  let overlay: HTMLElement

  let lastScrollTop = 0

  const {
    canAnimate = true,
    canCloseOnOverlayClick = false,
    canWindowAnimate,
    children,
    className,
    closeButtonType = 'none',
    height,
    isClosable = true,
    onClose,
    overlay: overlayType = EOverlay.Visible,
    size,
    width,
    willRemove = true,
    willRestoreScrollBar
  } = props

  const customCloseBtn: HTMLElement = <div className="sen-modal__close-button" />

  /** A modalt bezáró elemek klikkelésére iratkozás. */
  function initModalClosers (): void {
    // az össszes data-sen-modal-close attribútummal rendelkező elem.
    const elems = Array.from(modal.querySelectorAll('[data-sen-modal-close]'))

    // minden elemre rákötjük az onClickElemet
    elems.forEach((elem) => {
      elem.addEventListener('click', () => {
        DOM.dispatch(modal, EModalEvent.Close)
      })
    })
  }

  /** Itt történik meg valóban a bezárás. */
  function closeModal (): void {
    modal.style.display   = 'none'
    overlay.style.opacity = '0'

    if (size === ESize.Responsive && willRestoreScrollBar) {
      document.body.style.paddingRight = '0'
      document.body.style.overflowY    = '' // ne visible legyen, hanem csak üssük ki a hidden-t!
      scroll.toY(lastScrollTop, 'auto')
    }

    // ha clear, akkor eltávolítjuk a modalt
    if (willRemove) {
      DOM.remove(modal)
    }

    if (onClose) {
      onClose()
    }
  }

  /**
   * Modal bezárását kiváltó esemény callback-je.
   * @param e - Event.
   */
  async function onCloseModal (e: Event): Promise<void> {
    const isForceAnimate = (e as CustomEvent).detail as boolean | undefined

    if (closeButtonType === 'custom') {
      customCloseBtn.removeAttribute('data-clickable')
    }

    // ha van animáció
    if (canAnimate || isForceAnimate) {
      modal.setAttribute('data-lock-events', '')

      await Promise.all([
        animateModalPart(overlay, 'sen-modal-fade-out'),
        canWindowAnimate
          ? animateModalPart(window, 'sen-modal-slide-out')
          : Promise.resolve()
      ])

      modal.removeAttribute('data-lock-events')
    }

    closeModal()
  }

  /** Kattintható és bezárható. */
  function setInteractive (): void {
    // Ha customCloseButton van.
    if (modal.querySelector('.sen-modal__close-button')) {
      (modal.querySelector('.sen-modal__close-button') as HTMLElement).setAttribute('data-clickable', '')
    }

    // Csak akkor figyeli a data-sen-modal-close attribútummal.
    // Rendelkező elemek click-jét, ha van látható modal.
    initModalClosers()
  }

  /** Modal megjelenítését figyelő esemény callbackje. */
  async function onShow (): Promise<void> {
    lastScrollTop = DOM.getScrollTop()

    if (willRestoreScrollBar) {
      document.body.style.paddingRight = `${ DOM.getScrollbarWidth() }px`
      document.body.style.overflowY    = 'hidden'
    }

    modal.style.display   = 'block'
    overlay.style.opacity = '1'

    if (canAnimate) {
      modal.setAttribute('data-lock-events', '')

      await Promise.all([
        animateModalPart(overlay, 'sen-modal-fade-in'),
        canWindowAnimate ? animateModalPart(window, 'sen-modal-slide-in') : Promise.resolve()
      ])

      modal.removeAttribute('data-lock-events')
    }

    setInteractive()
  }

  const style = {
    ...(width ? { width } : {}),
    ...(height ? { height } : {})
  } as const

  customCloseBtn.setAttribute('data-sen-modal-close', '')

  window = (
    <div
      className="sen-modal__window"
      style={ style }
      data-testid="game-over-modal"
      onClick={ (e: Event): void => {
        e.stopPropagation()
      } }
      { ...(size ? { 'data-size': size } : {}) }
    >
      { closeButtonType === 'none' || !isClosable ? null : customCloseBtn }

      <div className="sen-modal__content">
        { children ?? null }
      </div>
    </div>
  )

  overlay = (
    <div
      className={ styles.senModalOverlay }
      // ha a méretünk Responsive, closeable és nincs closeButtonunk, akkor erre kattintva bezáródik a modal.
      { ...(canCloseOnOverlayClick
        ? { 'data-sen-modal-close': '' }
        : {}
      ) }
    >
      { window }
    </div>
  )

  modal = (
    <div
      id={ getActModalId() }
      className={ [ 'sen-modal', className ?? '' ] }
      data-overlay={ overlayType }
    >
      { overlay }
    </div>
  )

  if (isClosable) {
    modal.addEventListener(EModalEvent.Close, onCloseModal)
  }

  modal.addEventListener(EModalEvent.Show, onShow)

  return modal
}
